import React from 'react';

const PartA6 = ({ data, pageNumber = 1 }) => {
    // Handle both page 1 and page 2 of Part A6
    const isPage1 = pageNumber === 1;
    
    if (isPage1) {
        return <PartA6Page1 data={data} />;
    } else {
        return <PartA6Page2 data={data} />;
    }
};

// Page 1: Plan to do Online Courses
const PartA6Page1 = ({ data }) => {
    // Course data matching the network diagram
    const courseData = [
        { key: 'course_public_speaking', label: 'Public Speaking and Storytelling', color: '#4a90e2' },
        { key: 'course_formal_creative_writing', label: 'The Art of Formal and Creative Writing', color: '#2c3e50' },
        { key: 'course_health_nutrition_literacy', label: 'Health and Nutrition Literacy', color: '#3498db' },
        { key: 'course_environmental_sustainability', label: 'Environmental Sustainability', color: '#f39c12' },
        { key: 'course_citizen_politics_democracy', label: 'Citizen Politics in Democracy', color: '#8bc34a' },
        { key: 'course_mental_wellness', label: 'Mental Wellness', color: '#3498db' },
        { key: 'course_gender_sensitivity', label: 'Gender Sensitivity', color: '#e74c3c' },
        { key: 'course_citizenship_values', label: 'Citizenship Values', color: '#2c3e50' },
        { key: 'course_ethics_reasoning', label: 'Ethics and Reasoning', color: '#2c3e50' },
        { key: 'course_values_ethics_duties', label: 'Values, Ethics & Fundamental Duties', color: '#8bc34a' },
        { key: 'course_fundamentals_neuroscience', label: 'Fundamentals of Neuroscience', color: '#3498db' },
        { key: 'course_artistic_literacy', label: 'Artistic Literacy', color: '#e74c3c' },
        { key: 'course_coding', label: 'Coding', color: '#3498db' },
        { key: 'course_digital_marketing', label: 'Digital Marketing', color: '#2c3e50' },
        { key: 'course_computational_thinking', label: 'Computational Thinking', color: '#f39c12' },
        { key: 'course_entrepreneurship', label: 'Entrepreneurship', color: '#e74c3c' }
    ];

    return (
        <div className="report-page relative font-[poppins] bg-white" style={{
            width: '210mm',
            height: '297mm',
            padding: '20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.4',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative',
            breakInside: 'avoid',
            pageBreakInside: 'avoid'
        }}>
            {/* Orange corner triangles */}
            <div className="absolute top-0 right-0 w-0 h-0"
                 style={{
                     borderTop: '80px solid #f1905b',
                     borderLeft: '80px solid transparent'
                 }}>
            </div>
            <div className="absolute bottom-0 left-0 w-0 h-0"
                 style={{
                     borderBottom: '80px solid #F97316',
                     borderRight: '80px solid transparent'
                 }}>
            </div>

            {/* Header */}
            <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-orange-600 mb-2">Plan to do Online Courses</h2>
            </div>

            {/* Introduction Text */}
            <div className="mb-4 text-sm leading-relaxed">
                <p className="mb-3">
                    As part of your upskilling at this stage of school education, you are encouraged to do online 
                    courses and tutorials that help you develop different competencies. Given below is a list of 
                    possible areas you could do some research on before choosing courses that will be of interest.
                </p>
                <p>
                    At the beginning of the year, start planning courses you would like to do over the course of the year.
                </p>
            </div>

            {/* Warning Box */}
            <div className="flex items-center gap-3 bg-yellow-100 border-2 border-yellow-400 rounded p-3 mb-6">
                <div className="text-2xl">⚠️</div>
                <div className="font-semibold text-yellow-800">
                    Be sure to check with your teacher about which courses are suitable.
                </div>
            </div>

            {/* Course Network - Simplified Grid Layout for Report */}
            <div className="mb-6">
                <h4 className="font-bold text-orange-600 mb-3">Selected Online Courses</h4>
                <div className="grid grid-cols-4 gap-2">
                    {courseData.map((course, index) => (
                        <div
                            key={course.key}
                            className={`p-3 border-2 rounded text-center text-xs font-medium ${
                                data?.[course.key] 
                                    ? 'bg-orange-100 border-orange-400 text-orange-800' 
                                    : 'bg-gray-50 border-gray-300 text-gray-600'
                            }`}
                        >
                            <div className="mb-1">
                                {data?.[course.key] && <span className="text-orange-600 font-bold">✓</span>}
                            </div>
                            <div className="leading-tight">
                                {course.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Selected Courses Summary */}
            <div className="mb-6">
                <h4 className="font-bold text-orange-600 mb-3">Course Selection Summary</h4>
                <div className="bg-gray-50 p-4 border border-gray-300 rounded">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <span className="font-medium">Total Courses Available: </span>
                            <span className="font-bold">{courseData.length}</span>
                        </div>
                        <div>
                            <span className="font-medium">Courses Selected: </span>
                            <span className="font-bold text-orange-600">
                                {courseData.filter(course => data?.[course.key]).length}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Planning Notes */}
            <div className="mb-4">
                <h4 className="font-bold text-orange-600 mb-3">Planning Notes</h4>
                <div className="space-y-4">
                    <div>
                        <label className="block font-medium mb-2">Course Planning Notes:</label>
                        <div className="bg-gray-50 p-3 border border-gray-300 rounded min-h-[60px]">
                            {data?.course_planning_notes || ''}
                        </div>
                    </div>
                    <div>
                        <label className="block font-medium mb-2">Teacher Consultation Notes:</label>
                        <div className="bg-gray-50 p-3 border border-gray-300 rounded min-h-[60px]">
                            {data?.teacher_consultation_notes || ''}
                        </div>
                    </div>
                </div>
            </div>

            {/* Page Number */}
            <div className="absolute bottom-3 left-0 right-0 text-center text-xs font-medium">
                10
            </div>
        </div>
    );
};

// Page 2: Skills for Life
const PartA6Page2 = ({ data }) => {
    return (
        <div className="report-page relative font-[poppins] bg-white" style={{
            width: '210mm',
            height: '297mm',
            padding: '20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.4',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative',
            breakInside: 'avoid',
            pageBreakInside: 'avoid'
        }}>
            {/* Orange corner triangles */}
            <div className="absolute top-0 right-0 w-0 h-0"
                 style={{
                     borderTop: '80px solid #f1905b',
                     borderLeft: '80px solid transparent'
                 }}>
            </div>
            <div className="absolute bottom-0 left-0 w-0 h-0"
                 style={{
                     borderBottom: '80px solid #F97316',
                     borderRight: '80px solid transparent'
                 }}>
            </div>

            {/* Header */}
            <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-orange-600 mb-2">PART A (6)</h2>
                <h3 className="text-lg font-semibold text-gray-800">Skills for Life</h3>
            </div>

            {/* Introduction Text */}
            <div className="mb-6 text-sm leading-relaxed">
                <p>
                    We develop lots of important skills in our school years. Some of them are useful in school, but 
                    some of them are "life skills" that will be important to us in the future, no matter what we do. 
                    Think about one important life skill that you have gained and fill out the boxes below.
                </p>
            </div>

            {/* Skills Reflection Grid */}
            <div className="border-2 border-black mb-6">
                {/* Top Row - Two Columns */}
                <div className="flex border-b-2 border-black">
                    <div className="w-1/2 border-r-2 border-black">
                        <div className="bg-orange-400 text-white p-3 font-semibold text-center border-b-2 border-black min-h-[60px] flex items-center justify-center text-sm">
                            One important life skill you have learned at home, school or in the community
                        </div>
                        <div className="p-4 min-h-[150px] bg-white">
                            {data?.important_life_skill || ''}
                        </div>
                    </div>
                    
                    <div className="w-1/2">
                        <div className="bg-orange-400 text-white p-3 font-semibold text-center border-b-2 border-black min-h-[60px] flex items-center justify-center text-sm">
                            Experiences at home, school or in the community that helped you develop the skill
                        </div>
                        <div className="p-4 min-h-[150px] bg-white">
                            {data?.skill_development_experiences || ''}
                        </div>
                    </div>
                </div>

                {/* Middle Row - Full Width */}
                <div className="border-b-2 border-black">
                    <div className="bg-orange-400 text-white p-3 font-semibold text-center border-b-2 border-black">
                        Why is this skill important to you?
                    </div>
                    <div className="p-4 min-h-[120px] bg-white">
                        {data?.why_skill_important || ''}
                    </div>
                </div>

                {/* Bottom Row - Full Width */}
                <div>
                    <div className="bg-orange-400 text-white p-3 font-semibold text-center border-b-2 border-black">
                        What is another life skill you would like to develop and why do you think it will be important to you?
                    </div>
                    <div className="p-4 min-h-[150px] bg-white">
                        {data?.future_skill_to_develop || ''}
                    </div>
                </div>
            </div>

            {/* Skills Development Summary */}
            <div className="mb-4">
                <h4 className="font-bold text-orange-600 mb-3">Skills Development Summary</h4>
                <div className="bg-gray-50 p-4 border border-gray-300 rounded">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="font-medium">Current Life Skill Identified: </span>
                            <span className={data?.important_life_skill ? 'text-green-600 font-medium' : 'text-gray-500'}>
                                {data?.important_life_skill ? 'Yes' : 'Not specified'}
                            </span>
                        </div>
                        <div>
                            <span className="font-medium">Future Skill Goal Set: </span>
                            <span className={data?.future_skill_to_develop ? 'text-green-600 font-medium' : 'text-gray-500'}>
                                {data?.future_skill_to_develop ? 'Yes' : 'Not specified'}
                            </span>
                        </div>
                        <div>
                            <span className="font-medium">Development Experiences Noted: </span>
                            <span className={data?.skill_development_experiences ? 'text-green-600 font-medium' : 'text-gray-500'}>
                                {data?.skill_development_experiences ? 'Yes' : 'Not specified'}
                            </span>
                        </div>
                        <div>
                            <span className="font-medium">Importance Understood: </span>
                            <span className={data?.why_skill_important ? 'text-green-600 font-medium' : 'text-gray-500'}>
                                {data?.why_skill_important ? 'Yes' : 'Not specified'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Page Number */}
            <div className="absolute bottom-3 left-0 right-0 text-center text-xs font-medium">
                9
            </div>
        </div>
    );
};

export default PartA6;